import React, { forwardRef, useEffect, useState } from "react";
import TextInput, { TextInputProps } from "../TextInput/TextInput";

type PhoneInputProps = Omit<TextInputProps, "onChange"> & {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** PhoneInput is responsible for rendering a phone input field with automatic formatting */
const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ onChange, value, defaultValue, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState<string>(
      value != null ? formatPhone(String(value)) : defaultValue != null ? formatPhone(String(defaultValue)) : ""
    );

    useEffect(() => {
      setDisplayValue(value != null ? formatPhone(String(value)) : "");
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhone(e.target.value);
      setDisplayValue(formatted);
      onChange?.({ ...e, target: { ...e.target, value: formatted } });
    };

    return (
      <TextInput
        {...props}
        ref={ref}
        value={displayValue}
        onChange={handleChange}
        inputProps={{ maxLength: 14, ...props.inputProps }}
        placeholder="(___) ___-____"
      />
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
export { PhoneInput };